import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { RouteComponentProps } from 'react-router'
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useRouteMatch,
} from 'react-router-dom'
import Details from './Details'
import Checkout from './Checkout'
import About from './About'
import { useDispatch, useSelector } from 'react-redux'
import { OuterWrapper, PageLink, PageToggler } from './parts'
import { legacyPools } from '../../data/legacy-pools'
import Big from 'big.js'
import {
  setIsLaunch,
  setShowHeaderGradient,
} from '../../modules/miscUi/actions'
import {
  selectHeaderHeight,
  selectIsCheckoutScreen,
  selectIsDarkThemeState,
} from '../../modules/miscUi/selectors'
import { IPool } from '../../types/reducerTypes'
import { getPoolBySlug } from '../../utils'
import { selectAccount } from '../../modules/web3/selectors'
import useWeb3 from '../../hooks/useWeb3'
import useDynamicMaster from '../../hooks/useDynamicMaster'
import { amountFromWei } from '../../utils/amount'
import { USDB_DECIMALS } from '../../config'
import useMissionControlPoolStateManagement from '../../hooks/useMissionControlPoolStateManagement'
import {
  selectCurrentProject,
  selectProjectHasTasks,
  selectTotalAllocation,
} from '../../modules/missionControl/selectors'
import { theseus } from '../../api/theseus'
import { setCurrentProject } from '../../modules/missionControl/slice'
import Missions from './Missions'
import AnimatedLoader from '../../shared_components/AnimatedLoader'
import { HelmetProvider } from '../../App'
import NotFound from './NotFound'
import { setIsEthRaise } from '../../modules/web3/actions'
import Geoblocked from '../DropDetails/Geoblocked'
// import useNetworkId from '../../hooks/useNetworkId'
import DoubleHero from '../../shared_components/DoubleHero'
import TopInfo from './TopInfo'
import { useWindowSize } from '../../hooks/useWindowSize'

export enum CardThemes {
  OPEN = 'OPEN',
  UPCOMING = 'UPCOMING',
  CLOSED = 'CLOSED',
}

interface MatchParams {
  slug: string
}

const PoolDetails: FC<RouteComponentProps<MatchParams>> = ({
  match: {
    params: { slug },
  },
}) => {
  const history = useHistory()
  const { url } = useRouteMatch()

  const dispatch = useDispatch()
  const legacyPool = legacyPools.find(
    (pool) => pool.cmsDetails.poolAddress === String(slug),
  ) as any

  const [pool, setPool] = useState<IPool | undefined>(legacyPool?.cmsData)

  const [geoblockedCountry, setGeoblockedCountry] = useState('')
  const isGeoBlocked = !!geoblockedCountry

  const totalAllocation = useSelector(selectTotalAllocation)

  useMissionControlPoolStateManagement()

  const headerHeight = useSelector(selectHeaderHeight)

  const isCheckout = useSelector(selectIsCheckoutScreen)

  const isDarkTheme = useSelector(selectIsDarkThemeState)

  const account = useSelector(selectAccount)

  const { blastProvider } = useWeb3()

  const { isVetted: isLabsPool } = pool ?? {}

  const raiseCurrency = pool?.poolSettings?.raiseCurrency ?? 'usdb'
  const isEthRaise = raiseCurrency === 'eth'

  const contractName = isLabsPool
    ? undefined
    : `TokenSale${isEthRaise ? 'ETH' : 'USDB'}`

  const topBlockRef = useRef<HTMLDivElement>(null)
  const { width: screenWidth } = useWindowSize()

  const isMobile = screenWidth < 1000

  const dynamicMaster = useDynamicMaster(
    pool?.poolAddress,
    blastProvider,
    contractName,
  )

  const currentProject = useSelector(selectCurrentProject)
  const projectHasTasks = useSelector(selectProjectHasTasks)

  const [isTogglerFixed, setIsTogglerFixed] = useState(false)
  const [isAboutPage, setIsAboutPage] = useState(true)
  const [isMissionsPage, setIsMissionsPage] = useState(false)
  const [initLoad, setInitLoad] = useState(true)
  const [isLoading, setIsLoading] = useState(!legacyPool?.cmsData)

  const [calculatedMaxAllocation, setCalculatedMaxAllocation] = useState<
    number | null
  >(null)
  const [calculatedTaxFreeAllocation, setCalculatedTaxFreeAllocation] =
    useState(0)

  const [previousInvestment, setPreviousInvestment] = useState(0)
  const [taxRate, setTaxRate] = useState(0)
  const [shouldPoll, setShouldPoll] = useState(false)

  const isTbc = pool?.isTbc

  const navItems = [
    {
      title: 'about the project',
      path: url + '/about',
    },
    {
      title: 'project details',
      path: url + '/details',
      shouldHide: isTbc,
    },
    {
      title: 'missions',
      path: url + '/missions',
      shouldHide: isTbc,
    },
  ]

  useEffect(() => {
    if (!initLoad) history.push(navItems[0].path)
    else setInitLoad(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getAndSetCurrentProject = useCallback(async () => {
    try {
      const { data } = await theseus.get(`/project/b2c/${slug}`)
      if (!data?.project?.id) throw new Error('Project not found')
      const { project } = data
      dispatch(setCurrentProject(project))
    } catch (error) {
      console.error(error)
    }
  }, [dispatch, slug])

  const getAndSetPool = useCallback(async () => {
    try {
      const {
        data: { pool: poolFromCms },
      } = await getPoolBySlug(slug)
      if (poolFromCms?.geoblockedCountry) {
        setGeoblockedCountry(poolFromCms.geoblockedCountry)
        return
      }
      if (poolFromCms.slug !== slug) throw new Error('Pool not found')
      setPool(poolFromCms)
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }, [slug])

  useEffect(() => {
    if (currentProject?.slug === slug) return
    getAndSetCurrentProject()
  }, [currentProject, slug, getAndSetCurrentProject])

  useEffect(() => {
    if (!!pool) return
    getAndSetPool()
  }, [pool, slug, getAndSetPool])

  const dynamicMasterLoaded = !!dynamicMaster
  const baselineAllocation = pool?.poolSettings?.baselineAllocation
  const poolTaxRate = pool?.poolSettings?.taxRate

  const calculateAllocation = useCallback(async () => {
    try {
      const globalMaxAllocation = baselineAllocation ?? 0

      const setDefault = () => {
        setCalculatedMaxAllocation(globalMaxAllocation)
        setCalculatedTaxFreeAllocation(0)
      }

      if (!pool?.poolAddress) {
        setDefault()
        return
      }

      if (!dynamicMaster?.stakes || !dynamicMaster?.tokensaleTiers) {
        setDefault()
        return
      }

      if (!account) {
        setDefault()
        return
      }

      const alreadyInvested = await dynamicMaster?.stakes(account)

      const alreadyInvestedInUSDC = isLabsPool
        ? Number(alreadyInvested['amount'])
        : amountFromWei(
            Big(alreadyInvested['amount']),
            USDB_DECIMALS,
          ).toNumber()

      setPreviousInvestment(alreadyInvestedInUSDC)

      const whitelistTaxFreeAllocation = !account
        ? '0'
        : await dynamicMaster?.tokensaleTiers(account)

      setTaxRate(isLabsPool ? poolTaxRate : 0)

      setCalculatedMaxAllocation(
        isLabsPool
          ? baselineAllocation !== totalAllocation
            ? !!Number(whitelistTaxFreeAllocation)
              ? Number(whitelistTaxFreeAllocation)
              : baselineAllocation
            : totalAllocation
          : !!Number(whitelistTaxFreeAllocation)
          ? amountFromWei(whitelistTaxFreeAllocation, USDB_DECIMALS).toNumber()
          : globalMaxAllocation,
      )
      setCalculatedTaxFreeAllocation(0)
    } catch (error) {
      console.error(error, 'in calculateAllocation', pool?.poolAddress)
      setCalculatedMaxAllocation(baselineAllocation ?? 0)
    }
  }, [
    poolTaxRate,
    dynamicMaster,
    account,
    isLabsPool,
    baselineAllocation,
    pool?.poolAddress,
    totalAllocation,
  ])

  const poolIsClosed =
    pool?.privateSale?.end &&
    new Date(pool?.privateSale?.end).getTime() - new Date().getTime() <
      // 5 * 1000 * 60 // five minutes
      1 * 1000 * 60 // 1 minute

  useEffect(() => {
    if (!shouldPoll) return
    calculateAllocation()
    let timer = setInterval(() => {
      calculateAllocation()
    }, 15000)
    return () => {
      let timerClone = timer
      clearTimeout(timerClone)
      timer = null
      timerClone = null
    }
  }, [shouldPoll, calculateAllocation])

  useEffect(() => {
    if (!dynamicMasterLoaded || poolIsClosed) {
      setShouldPoll(false)
      return
    }
    if (!shouldPoll) setShouldPoll(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicMasterLoaded, poolIsClosed, shouldPoll])

  const pathSplit = history.location.pathname.split('/')

  useEffect(() => {
    const endOfPath = pathSplit[pathSplit.length - 1]
    setIsAboutPage(endOfPath !== 'details' && endOfPath !== 'missions')
    setIsMissionsPage(endOfPath === 'missions')
  }, [url, history, pathSplit])

  useEffect(() => {
    window.scrollTo(0, 0)
    const topBanner = pool?.media?.topBanner?.url
    dispatch(setShowHeaderGradient(!!topBanner))

    return () => {
      dispatch(setShowHeaderGradient(false))
    }
  }, [dispatch, pool, isDarkTheme])

  const topBanner = pool?.media?.topBanner?.url ?? `/hero-cover-labs.png`

  useEffect(() => {
    if (typeof isLabsPool === 'undefined') return
    dispatch(setIsLaunch(!isLabsPool))
    return () => {
      dispatch(setIsLaunch(false))
    }
  }, [isLabsPool, dispatch])

  useEffect(() => {
    dispatch(setIsEthRaise(raiseCurrency === 'eth'))
    return () => {
      dispatch(setIsEthRaise(false))
    }
  }, [dispatch, raiseCurrency])

  useEffect(() => {
    if (!isMobile) return
    if (!topBlockRef.current) return
    if (!isTogglerFixed) return
    const topBlockHeight = topBlockRef.current.clientHeight
    const currentScrollPosition = window.scrollY
    const desiredScrollPosition = topBlockHeight + 220
    if (Math.abs(currentScrollPosition - desiredScrollPosition) < 100) return
    window.scrollTo(0, desiredScrollPosition)
  }, [isMobile, pathSplit, topBlockRef, isTogglerFixed])

  if (isLoading) return <AnimatedLoader onlyWhite />
  if (isGeoBlocked) return <Geoblocked country={geoblockedCountry} />
  if (!pool) return <NotFound />

  return (
    <>
      <HelmetProvider title={`ZAP | ${pool.name} Fundraise`} />
      <DoubleHero imageSrc={topBanner} headerHeight={headerHeight} />
      {isLoading ? (
        <OuterWrapper className="min-h-screen flex flex-col md:pt-16" />
      ) : (
        <OuterWrapper>
          {!isCheckout && (
            <>
              <TopInfo
                pool={pool}
                calculatedMaxAllocation={calculatedMaxAllocation}
                topBlockRef={topBlockRef}
              />
              <PageToggler
                isFixed={isTogglerFixed}
                setIsFixed={setIsTogglerFixed}
              >
                <PageLink
                  onClick={() => setIsAboutPage(true)}
                  to={url + '/about'}
                  $isActive={isAboutPage}
                  $isFirst
                >
                  About
                </PageLink>
                <PageLink
                  onClick={() => setIsAboutPage(false)}
                  to={url + '/details'}
                  $isActive={!isAboutPage && !isMissionsPage}
                >
                  Raise Details
                </PageLink>
                {projectHasTasks && (
                  <PageLink
                    onClick={() => setIsAboutPage(false)}
                    to={url + '/missions'}
                    $isActive={isMissionsPage}
                  >
                    Missions
                  </PageLink>
                )}
              </PageToggler>
            </>
          )}
          <Switch>
            <Route path={url + '/checkout'}>
              <Checkout
                pool={pool}
                calculatedMaxAllocation={calculatedMaxAllocation ?? 0}
                calculatedTaxFreeAllocation={calculatedTaxFreeAllocation}
                whitelistAllocation={0}
                taxRate={taxRate}
                previousInvestment={previousInvestment}
              />
            </Route>
            <Route path={url + '/about'}>
              <About pool={pool} />
            </Route>
            <Route path={url + '/details'}>
              <Details pool={pool} />
            </Route>
            <Route path={url + '/missions'}>
              {projectHasTasks ? (
                <Missions pool={pool} />
              ) : (
                <Redirect to={url + '/about'} />
              )}
            </Route>
            <Route
              path={url + '/*'}
              render={() => <Redirect to={url + '/about'} />}
            />
            <Route path="*" render={() => <Redirect to={url + '/about'} />} />
          </Switch>
        </OuterWrapper>
      )}
    </>
  )
}

export default PoolDetails
